/**
 * Regole pure della lista "Ordini Clienti" (vista v_all_orders).
 *
 * Qui stanno le scelte che prima erano sparse nello store e nel componente e
 * che hanno prodotto quattro difetti in produzione:
 * - la ricerca metteva il testo digitato dentro `.or(...)` senza apici: una
 *   virgola o una parentesi ("Rossi, Mario", "ORD (vecchio)") rompevano la query;
 * - i filtri Stato e Operatore erano a scelta multipla ma si applicava solo il
 *   primo valore;
 * - l'aggiornamento in tempo reale ricaricava sempre pagina 1;
 * - il link dalla scheda Cliente usava un parametro che il Magazzino non legge.
 */
import type { OrderChannel, UnifiedOrderFilters } from '../types/orders';
import { productSearchFilter } from './productSearch';

/** Valore speciale del filtro Operatore: ordini senza nessuno assegnato. */
export const UNASSIGNED_FILTER_VALUE = 'unassigned';

/** Campi su cui cerca il box "Cerca" della lista ordini. */
export const ORDER_SEARCH_FIELDS = ['order_number', 'external_number', 'customer_name', 'customer_email'];

/**
 * Filtro per `.or(...)` della ricerca ordini, con il testo protetto tra apici.
 * Ritorna null se non c'e' niente da cercare (solo spazi compresi).
 */
export function orderSearchFilter(term: string | null | undefined): string | null {
  // "#113520" come lo scrive WordPress: il negozio salva il numero senza cancelletto
  const t = term?.trim().replace(/^#+/, '');
  if (!t) return null;
  return productSearchFilter(t, ORDER_SEARCH_FIELDS);
}

/** Tutti gli stati scelti: unisce il vecchio campo singolo e quello multiplo, senza doppioni. */
export function selectedStatuses(filters: Pick<UnifiedOrderFilters, 'status' | 'statuses'>): string[] {
  const all = [...(filters.statuses ?? []), ...(filters.status ? [filters.status] : [])];
  return [...new Set(all.filter(Boolean))];
}

export type AssigneeClause =
  | { type: 'none' }
  | { type: 'unassigned' }
  | { type: 'in'; ids: string[] }
  /** "Non assegnati" insieme ad altri operatori: serve un OR */
  | { type: 'or'; expr: string };

/**
 * Come filtrare per Operatore. Accetta sia il vecchio campo singolo
 * (`assignedToId`) sia la lista (`assignedToIds`).
 */
export function assigneeClause(
  filters: Pick<UnifiedOrderFilters, 'assignedToId' | 'assignedToIds'>,
): AssigneeClause {
  const all = [...(filters.assignedToIds ?? []), ...(filters.assignedToId ? [filters.assignedToId] : [])];
  const unique = [...new Set(all.filter(Boolean))];
  if (unique.length === 0) return { type: 'none' };
  const wantsUnassigned = unique.includes(UNASSIGNED_FILTER_VALUE);
  const ids = unique.filter(v => v !== UNASSIGNED_FILTER_VALUE);
  if (wantsUnassigned && ids.length === 0) return { type: 'unassigned' };
  if (!wantsUnassigned) return { type: 'in', ids };
  // Gli id sono uuid: nessun carattere riservato da proteggere.
  return { type: 'or', expr: `assigned_to.is.null,assigned_to.in.(${ids.join(',')})` };
}

/**
 * Canale con cui aprire un ordine arrivato da link (`?order=<id>&channel=...`).
 * Vince il canale della riga gia' caricata, poi quello del link, poi 'manual'
 * (l'unico da cui arrivano i link generati dalle offerte).
 */
export function resolveDeepLinkChannel(
  channelParam: string | null | undefined,
  loadedChannel: OrderChannel | undefined,
): OrderChannel {
  if (loadedChannel) return loadedChannel;
  if (channelParam === 'woocommerce' || channelParam === 'manual' || channelParam === 'shopify') return channelParam;
  return 'manual';
}

/**
 * Link alla lista Ordini Clienti del Magazzino. Il Magazzino legge `section`
 * (non `tab`): con `?tab=ordini` si apriva la Dashboard e l'ordine no.
 */
export function ordersSectionUrl(params: { orderId?: string; channel?: OrderChannel; clientId?: string } = {}): string {
  const qs = new URLSearchParams({ section: 'orders' });
  if (params.orderId) qs.set('order', params.orderId);
  if (params.channel) qs.set('channel', params.channel);
  if (params.clientId) qs.set('clientId', params.clientId);
  return `/magazzino?${qs.toString()}`;
}

/** Ordine arrivato da un negozio online (WooCommerce o Shopify), non inserito a mano. */
export function isStoreChannel(channel: OrderChannel | string | null | undefined): boolean {
  return !!channel && channel !== 'manual';
}

export const CHANNEL_LABEL: Readonly<Record<OrderChannel, string>> = Object.freeze({
  manual: 'Manuale', woocommerce: 'WooCommerce', shopify: 'Shopify',
});

/**
 * Riferimento dell'ordine nel negozio, per ritrovarlo li': "Shopify #1001", "WooCommerce #181".
 * Null per gli ordini manuali o senza numero del negozio.
 */
export function storeOrderRef(channel: OrderChannel | string | null | undefined, externalNumber: string | null | undefined): string | null {
  const n = externalNumber?.trim();
  if (!isStoreChannel(channel) || !n) return null;
  const label = CHANNEL_LABEL[channel as OrderChannel] ?? 'Negozio';
  return `${label} ${n.startsWith('#') ? n : `#${n}`}`;
}

/**
 * Numero da mostrare per un ordine: per gli ordini dei negozi quello del negozio
 * ("#113520"), lo stesso che vedono cliente e WordPress/Shopify; ORD-<anno>-... resta
 * il numero interno di Flusia. Manuali (o negozio senza numero): order_number.
 */
export function orderDisplayNumber(order: {
  order_number?: string | null;
  channel?: OrderChannel | string | null;
  external_number?: string | null;
}): string {
  const n = order.external_number?.trim();
  if (isStoreChannel(order.channel) && n) return n.startsWith('#') ? n : `#${n}`;
  return order.order_number ?? '';
}

/** Ritardo con cui la lista si ricarica dopo un evento realtime (raffiche di import). */
export const REALTIME_REFRESH_DEBOUNCE_MS = 400;
